import React, { useState, useEffect, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { FileText, Mic, Square, Loader2, Download, Save, Cloud } from 'lucide-react';
import { toast } from "sonner";
import axios from 'axios';

export function SessionRecorder({ courseId, courseTitle }) {
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState([]);
  const recognitionRef = useRef(null);

  // Simple keyword list for mock academic analysis
  const CS_KEYWORDS = [
    "Java", "Python", "Variable", "Function", "Method", "Class", "Object", 
    "Loop", "Array", "List", "String", "Integer", "Boolean", "Logic", 
    "Syntax", "Error", "Debug", "Algorithm", "Complexity", "Recursion", 
    "Stack", "Queue", "Tree", "Graph", "Big O", "Inheritance", "Polymorphism"
  ];

  useEffect(() => {
    // Check browser support
    if (!('webkitSpeechRecognition' in window) && !('speechRecognition' in window)) {
      console.warn("Speech recognition not supported in this browser.");
      return;
    }

    // Initialize Speech Recognition
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = 'en-US'; // Default to English

    recognition.onresult = (event) => {
      const transcriptText = Array.from(event.results)
        .slice(event.resultIndex)
        .map(result => result[0].transcript)
        .join('');
      
      if (transcriptText.trim()) {
        const timestamp = new Date().toLocaleTimeString();
        setTranscript(prev => [...prev, `[${timestamp}] User: ${transcriptText}`]);
      }
    };

    recognition.onerror = (event) => {
      console.error("Speech recognition error", event.error);
      if (event.error === 'not-allowed') {
        toast.error("Microphone access denied.");
        setIsRecording(false);
      }
    };

    recognition.onend = () => {
      if (isRecording) {
        // Restart if it stops unexpectedly while 'recording' state is true
        try {
            recognition.start();
        } catch (e) {
            // ignore start errors
        }
      }
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) recognitionRef.current.stop();
    };
  }, [isRecording]);

  const toggleRecording = () => {
    if (!recognitionRef.current) {
        toast.error("Speech recognition not supported in this browser.");
        return;
    }

    if (isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
      toast.info("Session recording paused.");
    } else {
      try {
        recognitionRef.current.start();
        setIsRecording(true);
        toast.success("Session recording started. Speak clearly!");
      } catch (e) {
        console.error(e);
      }
    }
  };

  const generateAcademicSummary = (fullText) => {
    // Simple keyword extraction to simulate "AI Analysis"
    const detectedTopics = CS_KEYWORDS.filter(keyword => 
        fullText.toLowerCase().includes(keyword.toLowerCase())
    );

    const uniqueTopics = [...new Set(detectedTopics)];
    
    const topicList = uniqueTopics.length > 0 
        ? uniqueTopics.join(", ") 
        : "General Computer Science Concepts";

    return `
================================================================
ACADEMIC SESSION SUMMARY
================================================================
Course Domain: Computer Science & Software Engineering
Session Date: ${new Date().toLocaleDateString()}
Analyzed Topics: ${topicList}

EXECUTIVE SUMMARY:
The session primarily focused on the practical application and theoretical understanding of the topics listed above. The discourse suggests an engagement with core programming principles, likely covering syntax validation, logical structuring of code, and problem-solving methodologies relevant to the course curriculum.

KEY LEARNING OUTCOMES:
1. Demonstrated engagement with ${uniqueTopics[0] || "fundamental"} concepts.
2. Explored problem-solving strategies related to the discussed topics.
3. Verbalized logic and code structure for validation.

RECOMMENDED NEXT STEPS:
- Review the documentation for: ${topicList}
- Practice implementation of discussed concepts in the code editor.
================================================================
    `;
  };

  const handleSaveToCloud = async () => {
    if (transcript.length === 0) {
        toast.warning("No conversation recorded yet.");
        return;
    }

    const token = localStorage.getItem('faheem_token');
    if (!token) {
        toast.error("Please login to save sessions");
        return;
    }

    try {
        const fullTranscript = transcript.join('\n');
        const academicSummary = generateAcademicSummary(fullTranscript);
        
        // Extract topics from the transcript
        const detectedTopics = CS_KEYWORDS.filter(keyword => 
            fullTranscript.toLowerCase().includes(keyword.toLowerCase())
        );
        const uniqueTopics = [...new Set(detectedTopics)];

        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

        // Estimate duration (rough calculation based on transcript length)
        const estimatedDuration = Math.max(1, Math.round(transcript.length / 2));

        // Save session summary
        const response = await axios.post(
            `${BACKEND_URL}/api/sessions/save?access_token=${token}`,
            {
                course_id: courseId || "unknown",
                course_title: courseTitle || "Unknown Course",
                transcript: fullTranscript,
                summary: academicSummary,
                topics: uniqueTopics,
                duration_minutes: estimatedDuration
            }
        );

        const sessionId = response.data.session?.id;

        // Record learning activity
        if (sessionId) {
            await axios.post(
                `${BACKEND_URL}/api/analytics/activity?access_token=${token}`,
                {
                    activity_type: "session_record",
                    course_id: courseId || "unknown",
                    session_id: sessionId,
                    duration_minutes: estimatedDuration,
                    metadata: {
                        topics_count: uniqueTopics.length,
                        transcript_length: fullTranscript.length,
                        course_title: courseTitle
                    }
                }
            );
        }

        toast.success("Session saved to your profile! 🎉");
        
        // Optionally download as well
        handleDownloadSummary();
    } catch (error) {
        console.error("Failed to save session:", error);
        toast.error("Failed to save session to cloud");
    }
  };

  const handleDownloadSummary = () => {
    if (transcript.length === 0) {
        toast.warning("No conversation recorded yet.");
        return;
    }

    const fullTranscript = transcript.join('\n');
    const academicSummary = generateAcademicSummary(fullTranscript);

    const content = `
COURSE SESSION TRANSCRIPT
Date: ${new Date().toLocaleString()}
----------------------------------------

${fullTranscript}

----------------------------------------
${academicSummary}
    `.trim();

    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Session_Transcript_${new Date().toISOString().slice(0,10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    toast.success("Transcript downloaded!");
  };

  return (
    <div className="flex items-center gap-2">
        {isRecording && (
            <div className="flex items-center gap-2 mr-2 px-2 py-1 bg-red-50 text-red-600 rounded-full text-xs animate-pulse border border-red-100">
                <div className="w-2 h-2 bg-red-500 rounded-full" />
                Recording...
            </div>
        )}
        
        <Button 
            variant={isRecording ? "destructive" : "secondary"}
            size="sm" 
            className="hidden md:flex gap-2 shadow-sm"
            onClick={toggleRecording}
        >
            {isRecording ? <Square className="w-4 h-4 fill-current" /> : <Mic className="w-4 h-4" />}
            {isRecording ? "Stop Recording" : "Record Session"}
        </Button>

        {transcript.length > 0 && (
            <>
                <Button 
                    variant="default" 
                    size="sm" 
                    className="hidden md:flex gap-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-sm"
                    onClick={handleSaveToCloud}
                >
                    <Cloud className="w-4 h-4" />
                    Save to Profile
                </Button>
                <Button 
                    variant="ghost" 
                    size="sm" 
                    className="hidden md:flex gap-2 text-slate-600 hover:text-blue-600 border border-transparent hover:border-blue-100 hover:bg-blue-50"
                    onClick={handleDownloadSummary}
                >
                    <Download className="w-4 h-4" />
                    Download
                </Button>
            </>
        )}
    </div>
  );
}
